import { useState, useEffect, useMemo } from 'react';
import { useDataLayer } from "../../../../Contexts//DataLayer.context";
import { usePlayer } from '../../../../Contexts/playerData.context';

import { cflObject } from "../../../../utils/loadFunctions.ts";
import { nflObject } from "../../../../utils/loadFunctions.ts";
import FBXCalendar from "../../../Universals/FBXCalendar/fbxcalendar.component.jsx";
import { getEasternDateParts } from "../../../../utils/dateUtils.ts";

const Player = ({ currentPlayer, onModalChange, onPlayerUpdated }) => {
  const { schoolArray, agentArray, positionArray } = useDataLayer();
  const { setPlayerData } = usePlayer();

  // Local editable copy + snapshot of original
  const [player, setPlayer] = useState({});
  const [originalPlayer, setOriginalPlayer] = useState({});
  const [showSaveCancel, setShowSaveCancel] = useState(false);

  // Special Teams toggles (ls/pr/kr/st/g/h)
  const [savingST, setSavingST] = useState({});

  const [openCalendar, setOpenCalendar] = useState(null);
  const handleToggleCalendar = (fieldName) => {
    setOpenCalendar((prev) => (prev === fieldName ? null : fieldName));
  };

  const displayProJersey = (value) => {
    const text = String(value ?? '').trim();
    if (text === '0') return '';
    if (text === '-1') return '0';
    return text;
  };

  const SPECIAL_TEAMS = [
    { field: 'ls', label: 'LS' },
    { field: 'pr', label: 'PR' },
    { field: 'kr', label: 'KR' },
    { field: 'st', label: 'ST' },
    { field: 'g', label: 'G' },
    { field: 'h', label: 'H' }, // holder
  ];

  const normalize01 = (v) => (String(v) === '1' ? '1' : '0');

  // ✅ FBXID-safe comparison (handles string/number mismatches)
  const sameFBXID = (a, b) => {
    const aa = String(a ?? '').trim();
    const bb = String(b ?? '').trim();
    return aa !== '' && bb !== '' && aa === bb;
  };

  // ✅ Update playerData/playerArray safely
  const patchPlayerInArray = (FBXID, patch) => {
    setPlayerData((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.map((p) => (sameFBXID(p?.FBXID, FBXID) ? { ...p, ...patch } : p));
    });
  };

  // Save a small patch (reuses your existing tomBox.php saveTomBox API)
  const saveTomBoxPatch = async (FBXID, patch) => {
    const response = await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        script: 'saveTomBox',
        FBXID,
        data: patch,
      }),
    });
    const data = await response.json();
    return data;
  };

  const toggleSpecialTeamAndSave = async (field) => {
    const FBXID = player?.FBXID;
    if (!FBXID) return;

    // compute next value from current
    const current = normalize01(player?.[field]);
    const nextVal = current === '1' ? '0' : '1';

    // optimistic UI
    const prevSnapshot = player;
    const nextLocal = { ...player, [field]: nextVal };
    setPlayer(nextLocal);

    setSavingST((prev) => ({ ...prev, [field]: true }));
    try {
      await saveTomBoxPatch(FBXID, { [field]: nextVal });

      // commit as "original" too, so it doesn’t keep showing as unsaved
      setOriginalPlayer((prev) => ({ ...prev, [field]: nextVal }));

      // ✅ keep list in sync (playerArray / playerData)
      patchPlayerInArray(FBXID, { [field]: nextVal });

      if (typeof onPlayerUpdated === 'function') onPlayerUpdated(nextLocal);
    } catch (err) {
      console.log('Special teams save failed:', err);
      // revert UI if save fails
      setPlayer(prevSnapshot);
    } finally {
      setSavingST((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Sync from currentPlayer only when it changes
  useEffect(() => {
    setPlayer(currentPlayer || {});
    setOriginalPlayer(currentPlayer || {});
  }, [currentPlayer]);

  // Build dropdown options from context positions (robust to strings/objects)
  // options: POS_DESC as label, POS_SPEC_CODE as value
  const positionOptions = useMemo(() => {
    const arr = Array.isArray(positionArray) ? positionArray : [];

    // sort by SortOrder (string numbers like "10")
    const sorted = [...arr].sort((a, b) => {
      const na = parseInt(a?.SortOrder ?? '9999', 10);
      const nb = parseInt(b?.SortOrder ?? '9999', 10);
      return na - nb;
    });

    // dedupe by code
    const seen = new Set();
    return sorted
      .map((p, idx) => {
        const code = String(p?.POS_SPEC_CODE ?? '').trim();
        const label = String(p?.POS_SPEC_CODE ?? code).trim();
        if (!code) return null;
        if (seen.has(code)) return null;
        seen.add(code);
        // keep Gen_Pos on the option for convenience (not required)
        return (
          <option key={`${code}-${idx}`} value={code} data-genpos={p?.Gen_Pos ?? ''}>
            {label}
          </option>
        );
      })
      .filter(Boolean);
  }, [positionArray]);

  // when Position changes: set colPos1 (code) and genPos from Gen_Pos; also mirror to GP
  const handlePosChange = (code) => {
    const item = (positionArray || []).find(
      (p) => String(p?.POS_SPEC_CODE ?? '').trim() === String(code).trim()
    );
    const gen = item?.Gen_Pos ? String(item.Gen_Pos).trim() : '';
    setPlayer((prev) => {
      const next = {
        ...prev,
        colPos1: code,
        genPos: gen,
      };
      if (next.colPos1 !== originalPlayer.colPos1 || next.genPos !== originalPlayer.genPos) {
        setShowSaveCancel(true);
      }
      return next;
    });
  };

  // Notify parent when edits are present
  useEffect(() => {
    if (typeof onModalChange === 'function') onModalChange(showSaveCancel);
  }, [showSaveCancel, onModalChange]);

  // Change tracking helpers
  const isChanged = (field) => (player?.[field] !== originalPlayer?.[field]);

  const inputClass = (field, base = "form-control fs-7") =>
    `${base} ${isChanged(field) ? "border border-danger border-2" : ""}`;

  const selectClass = (field, base = "form-select fs-7") =>
    `${base} ${isChanged(field) ? "border border-danger border-2" : ""}`;

  // Helper: safe age calc for 'YYYY-MM-DD'
  const computeAgeFromISO = (iso) => {
    if (!iso || iso === '0000-00-00') return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;

    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return null;

    // Use local date to avoid UTC off-by-one
    const dob = new Date(y, m - 1, d);
    if (Number.isNaN(dob.getTime())) return null;

    const today = getEasternDateParts();
    const todayYear = Number(today.year);
    const todayMonth = Number(today.month) - 1;
    const todayDay = Number(today.day);
    let age = todayYear - dob.getFullYear();
    const hasHadBirthday =
      todayMonth > dob.getMonth() ||
      (todayMonth === dob.getMonth() && todayDay >= dob.getDate());
    if (!hasHadBirthday) age--;

    return String(age);
  };

  // Minimal local change handler
  const handleChange = (field, value) => {
    setPlayer((prev) => {
      const next = { ...prev, [field]: value };

      // When DOB changes, recompute Age (read-only field)
      if (field === 'dob') {
        const ageStr = computeAgeFromISO(value);
        next.age = ageStr ?? '';
      }
      return next;
    });
    setShowSaveCancel(true);
  };

  // Cancel: revert to original values
  const handleCancel = () => {
    setPlayer(originalPlayer);
    setShowSaveCancel(false);
  };

  // Options for school selects
  const schoolOptions = useMemo(
    () =>
      (schoolArray || []).map((s, idx) => (
        <option key={idx} value={s.schoolcode}>
          {s.school_short_name}
        </option>
      )),
    [schoolArray]
  );

  // Options for agent select
  const agentOptions = useMemo(
    () =>
      (agentArray || []).map((a) => (
        <option key={String(a.ID)} value={String(a.ID)}>
          {a.LastFirstName || `(ID ${a.ID})`}
        </option>
      )),
    [agentArray]
  );

  // Save: send only changed fields, then reset "original" so borders clear
  const handleSave = async () => {
    const changedFields = {};
    for (const key in player) {
      if (player[key] !== originalPlayer[key]) {
        changedFields[key] = player[key];
      }
    }

    // Remove 'age' if present
    if ('age' in changedFields) delete changedFields.age;

    if (Object.keys(changedFields).length === 0) {
      setPlayer(originalPlayer);
      setShowSaveCancel(false);
      return;
    }

    try {
      await fetch('https://www.fbxchange.com/ReactPHP/tomBox.php', {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          script: 'saveTomBox',
          FBXID: player.FBXID,
          data: changedFields
        }),
      });

      const nextPlayer = { ...player };
      const ageStr = computeAgeFromISO(nextPlayer.dob);
      if (ageStr !== null) nextPlayer.age = ageStr;

      setPlayer(nextPlayer);
      setOriginalPlayer(nextPlayer);
      setShowSaveCancel(false);

      // ✅ update playerArray/playerData with FBXID-safe matching
      patchPlayerInArray(nextPlayer.FBXID, nextPlayer);

      if (typeof onPlayerUpdated === 'function') onPlayerUpdated(nextPlayer);
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div className="container-fluid">
      {showSaveCancel ? (
        <div className="d-flex justify-content-end align-items-center" style={{ minHeight: '40px' }}>
          <button className="btn btn-sm btn-secondary text-white me-2 w-25" onClick={handleCancel}>
            Escape
          </button>
          <button className="btn btn-sm btn-success text-white w-25" onClick={handleSave}>
            Save
          </button>
        </div>
      ) : (
        <div className="alert-warning text-center fw-bold d-flex align-items-center justify-content-center" style={{ minHeight: '30px' }}>
          When you Edit a Field - Save and Cancel Buttons will Appear
        </div>
      )}

      {/* Row: Name + Nationality */}
      <div className="d-flex gap-1">
        <div className="col-10">
          <label className="fs-7 ps-1">Last Name</label>
          <input
            className={inputClass('last', 'form-control fs-7 ps-1')}
            type="text"
            value={player.last || ''}
            onChange={(e) => handleChange('last', e.target.value)}
            placeholder="Last Name"
          />
        </div>
        <div className="col-10">
          <label className="fs-7 ps-1">First Name</label>
          <input
            className={inputClass('first', 'form-control fs-7 ps-1')}
            type="text"
            value={player.first || ''}
            onChange={(e) => handleChange('first', e.target.value)}
            placeholder="First Name"
          />
        </div>
        <div className="flex-grow-1">
          <label className="fs-7">School</label>
          <select
            className={selectClass('school', 'form-select fs-7 ps-1')}
            value={player.school || ''}
            onChange={(e) => handleChange('school', e.target.value)}
          >
            <option value="">School</option>
            {schoolOptions}
          </select>
        </div>
        <div className="col-3">
          <label className="fs-7 ps-1">Col #</label>
          <input
            className={inputClass('jersey', 'form-control fs-7 ps-1')}
            type="text"
            value={player.jersey || ''}
            onChange={(e) => handleChange('jersey', e.target.value)}
            placeholder="Col #"
          />
        </div>
        <div className="col-4">
          <label className="fs-7">Position</label>
          <select
            className={selectClass('colPos1', 'form-select fs-7 ps-1')}
            value={player.colPos1 || ''}
            onChange={(e) => handlePosChange(e.target.value, e)}
          >
            <option value="">Pos 1</option>
            {positionOptions}
          </select>
        </div>
        <div className="col-3">
          <label className="fs-7">Gen Pos</label>
          <input
            className={inputClass('genPos', 'form-control fs-7')}
            type="text"
            value={player.genPos || ''}
            placeholder="Gen Pos"
            readOnly
          />
        </div>
        <div className="col-4">
          <label className="fs-7">Pos 2</label>
          <select
            className={selectClass('colPos2', 'form-select fs-7 ps-1')}
            value={player.colPos2 || ''}
            onChange={(e) => handleChange('colPos2', e.target.value)}
          >
            <option value="">Pos 2</option>
            {positionOptions}
          </select>
        </div>
      </div>

      <div className="d-flex mt-2 gap-1">
        <div className="col-8">
          <label className="fs-7">DOB</label>
          <FBXCalendar
            fieldName="dob"
            placeholder="DOB"
            onDateChange={(value) => handleChange('dob', value)}
            isOpen={openCalendar === 'dob'}
            dateIn={!player.dob || player.dob === '0000-00-00' ? null : player.dob}
            minYear="1950"
            maxYear={getEasternDateParts().year}
            emptyDefaultYearOffset={-21}
            style={{ width: '100%' }}
            onToggleCalendar={handleToggleCalendar}
          />
        </div>

        <div className="col-3">
          <label className="fs-7">Age</label>
          <input
            className={inputClass('age', 'form-control fs-7 alert-secondary')}
            type="text"
            value={player.age || ''}
            onChange={(e) => handleChange('age', e.target.value)}
            placeholder="Age"
            readOnly
          />
        </div>

        <div className="col-8">
          <label className="fs-7">Previous School</label>
          <select
            className={selectClass('lastSchool', 'form-select fs-7 ps-1')}
            value={player.lastSchool || ''}
            onChange={(e) => handleChange('lastSchool', e.target.value)}
          >
            <option value="">Previous</option>
            {schoolOptions}
          </select>
        </div>

        <div className="col-8">
          <label className="fs-7">Second School</label>
          <select
            className={selectClass('lastSchoolTwo', 'form-select fs-7 ps-1')}
            value={player.lastSchoolTwo || ''}
            onChange={(e) => handleChange('lastSchoolTwo', e.target.value)}
          >
            <option value="">Second</option>
            {schoolOptions}
          </select>
        </div>

        <div className="col-7">
          <label className="fs-7">Team Location</label>
          <select
            className={selectClass('schoolCountry', 'form-select fs-7')}
            value={player.schoolCountry || ''}
            onChange={(e) => handleChange('schoolCountry', e.target.value)}
          >
            <option value="">Select</option>
            <option value="USA">USA</option>
            <option value="CAN">Canada</option>
            <option value="None">Non USA/Canada</option>
          </select>
        </div>

        <div className="col-3">
          <label className="fs-7 ps-1">Pro #</label>
          <input
            className={inputClass('proJersey', 'form-control fs-7 ps-1')}
            type="text"
            value={displayProJersey(player.proJersey)}
            onChange={(e) => handleChange('proJersey', e.target.value)}
            placeholder="Pro #"
          />
        </div>

        <div className="col-5">
          <label className="fs-7">Pro Position</label>
          <select
            className={selectClass('proPos', 'form-select fs-7 ps-1')}
            value={player.proPos || ''}
            onChange={(e) => handleChange('proPos', e.target.value)}
          >
            <option value="">Select Position</option>
            {positionOptions}
          </select>
        </div>

        <div className="flex-grow-1 d-flex flex-column justify-content-end">
          <label className="fs-7 ps-3 mb-1">JR Out</label>
          <button
            type="button"
            className={`btn btn-sm w-100 fs-7 ${player.jrOut === 'JR' ? 'btn-primary text-white' : 'btn-secondary text-lemon'}`}
            onClick={() => handleChange('jrOut', player.jrOut === 'JR' ? '' : 'JR')}
            style={{ height: '33px', lineHeight: '16px' }}
          >
            JR
          </button>
        </div>
      </div>

      <div className="d-flex mt-2 gap-1">
        <div className="col-8">
          <label className="fs-7">Home Town</label>
          <input
            className={inputClass('homeTown', 'form-control fs-7')}
            type="text"
            value={player.homeTown || ''}
            onChange={(e) => handleChange('homeTown', e.target.value)}
            placeholder="Home Town"
          />
        </div>

        <div className="col-7">
          <label className="fs-7">Home State/Prov</label>
          <input
            className={inputClass('homeState', 'form-control fs-7')}
            type="text"
            value={player.homeState || ''}
            onChange={(e) => handleChange('homeState', e.target.value)}
            placeholder="Home State"
          />
        </div>

        <div className="col-7">
          <label className="fs-7 ps-1">Home Country</label>
          <input
            className={inputClass('homeCountry', 'form-control fs-7 ps-1')}
            type="text"
            value={player.homeCountry || ''}
            onChange={(e) => handleChange('homeCountry', e.target.value)}
            placeholder="Home Country"
          />
        </div>

        <div className="flex-grow-1">
          <label className="fs-7 ps-1">Agent</label>
          <select
            className={selectClass('agentNum', 'form-select fs-7 ps-1')}
            value={player.agentNum || ''}
            onChange={(e) => handleChange('agentNum', e.target.value)}
          >
            <option value="">Select Agent</option>
            {agentOptions}
          </select>
        </div>
      </div>

      <div className="d-flex mt-2 gap-1">
        <div className="col-5">
          <label className="fs-7">CFL DY</label>
          <input
            className={inputClass('cflDraftYear', 'form-control fs-7 alert-danger')}
            type="text"
            value={player.cflDraftYear || ''}
            onChange={(e) => handleChange('cflDraftYear', e.target.value)}
            placeholder="CFL DY"
          />
        </div>
        <div className="col-6">
          <label className="fs-7">FCY</label>
          <input
            className={inputClass('optionYr', 'form-control fs-7 alert-danger')}
            type="text"
            value={player.optionYr || ''}
            onChange={(e) => handleChange('optionYr', e.target.value)}
            placeholder="FCY"
          />
        </div>

        <div className="col-14">
          <label className="fs-7 ps-1 fw-bold text-danger">CFL Draft Team</label>
          <select
            className={selectClass('cflDraftTeam', 'form-select fs-7 ps-1 alert-danger')}
            value={player.cflDraftTeam || ''}
            onChange={(e) => handleChange('cflDraftTeam', e.target.value)}
          >
            <option value="">Select Team</option>
            {Array.isArray(cflObject) &&
              cflObject.map((team, idx) => (
                <option key={team.Team || idx} value={team.Team}>
                  {team.Team_Name}
                </option>
              ))}
          </select>
        </div>

        <div className="col-4">
          <label className="fs-7 ps-1">CFL RND</label>
          <input
            className={inputClass('cflDraftRound', 'form-control fs-7 ps-1 alert-danger')}
            type="text"
            value={player.cflDraftRound || ''}
            onChange={(e) => handleChange('cflDraftRound', e.target.value)}
            placeholder="Round"
          />
        </div>
        <div className="col-4">
          <label className="fs-7 ps-1">CFL Pick</label>
          <input
            className={inputClass('cflDraftRoundPick', 'form-control fs-7 ps-1 alert-danger')}
            type="text"
            value={player.cflDraftRoundPick || ''}
            onChange={(e) => handleChange('cflDraftRoundPick', e.target.value)}
            placeholder="Pick"
          />
        </div>
        <div className="col-5">
          <label className="fs-7">CFL POS</label>
          <select
            className={selectClass('cflPos', 'form-select fs-7 ps-1 alert-danger')}
            value={player.cflPos || ''}
            onChange={(e) => handleChange('cflPos', e.target.value)}
          >
            <option value="">Select Position</option>
            {positionOptions}
          </select>
        </div>
        <div className="col-9">
          <label className="fs-7">CFL Standing</label>
          <select
            className={`${selectClass('impnip')} ${player.impnip === 'N'
              ? 'alert-danger'
              : player.impnip === 'G'
                ? 'alert-success'
                : player.impnip === 'V3' || player.impnip === 'V5'
                  ? 'alert-brown'
                  : ''
              }`}
            value={player.impnip || ''}
            onChange={(e) => handleChange('impnip', e.target.value)}
          >
            <option value="">Select Nationality</option>
            <option value="N">National</option>
            <option value="G">Global</option>
            <option value="V3">Veteran 3</option>
            <option value="V5">Veteran 5</option>
          </select>
        </div>
      </div>

      <div className="d-flex mt-2 gap-1">
        <div className="col-5">
          <label className="fs-7">NFL DY</label>
          <input
            className={inputClass('nflDraftYear', 'form-control fs-7 alert-info')}
            type="text"
            value={player.nflDraftYear || ''}
            onChange={(e) => handleChange('nflDraftYear', e.target.value)}
            placeholder="NFL DY"
          />
        </div>
        <div className="col-6"></div>

        <div className="col-14">
          <label className="fs-7 ps-1 fw-bold text-danger">NFL Draft Team</label>
          <select
            className={selectClass('nflDraftTeam', 'form-select fs-7 ps-1 alert-info')}
            value={player.nflDraftTeam || ''}
            onChange={(e) => handleChange('nflDraftTeam', e.target.value)}
          >
            <option value="">Select Team</option>
            {Array.isArray(nflObject) &&
              nflObject.map((team, idx) => (
                <option key={team.Team || idx} value={team.Team}>
                  {team.Team_Name}
                </option>
              ))}
          </select>
        </div>

        <div className="col-4">
          <label className="fs-7 ps-1">NFL RND</label>
          <input
            className={inputClass('nflDraftRound', 'form-control fs-7 ps-1 alert-info')}
            type="text"
            value={player.nflDraftRound || ''}
            onChange={(e) => handleChange('nflDraftRound', e.target.value)}
            placeholder="Round"
          />
        </div>
        <div className="col-4">
          <label className="fs-7 ps-1">NFL Pick</label>
          <input
            className={inputClass('nflDraftRoundPick', 'form-control fs-7 ps-1 alert-info')}
            type="text"
            value={player.nflDraftRoundPick || ''}
            onChange={(e) => handleChange('nflDraftRoundPick', e.target.value)}
            placeholder="Pick"
          />
        </div>
        <div className="col-5">
          <label className="fs-7">NFL POS</label>
          <select
            className={selectClass('nflPos', 'form-select fs-7 ps-1 alert-info')}
            value={player.nflPos || ''}
            onChange={(e) => handleChange('nflPos', e.target.value)}
          >
            <option value="">Select Position</option>
            {positionOptions}
          </select>
        </div>
      </div>

      <div className="d-flex mb-2 gap-1 mt-1">
        <div className="col-5">
          <label className="fs-7">Event POS</label>
          <select
            className={selectClass('eventPos', 'form-select fs-7 ps-1 alert-purple')}
            value={player.eventPos || ''}
            onChange={(e) => handleChange('eventPos', e.target.value)}
          >
            <option value="">E Pos</option>
            {positionOptions}
          </select>
        </div>

        <div className="col-6">
          <label className="fs-7 ps-1">Side</label>
          <input
            className={inputClass('eventSide', 'form-control fs-7 ps-1 alert-purple')}
            type="text"
            value={player.eventSide || ''}
            onChange={(e) => handleChange('eventSide', e.target.value)}
            placeholder="Side"
          />
        </div>

        <div className="col-23 d-flex flex-column justify-content-end" id="specialTeamsSpot">
          <div className="d-flex flex-column">
            <div className="fs-7 fw-bold text-dark ps-1 mb-1" style={{ lineHeight: '18px' }}>
              Special Teams
            </div>

            <div className="d-flex flex-nowrap gap-1">
              {SPECIAL_TEAMS.map(({ field, label }) => {
                const on = normalize01(player?.[field]) === '1';
                return (
                  <button
                    key={field}
                    type="button"
                    className={`btn btn-sm fs-7 col-6 ${on ? 'btn-primary text-white' : 'btn-outline-secondary'}`}
                    disabled={!!savingST?.[field]}
                    onClick={() => toggleSpecialTeamAndSave(field)}
                    style={{ height: '30px', padding: '3px 10px' }}
                    title={label}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="col">
          <label className="fs-7 ps-1">UFL24 Pos</label>
          <select
            className={selectClass('ufl24Pos', 'form-select fs-7 ps-1 alert-warning')}
            value={player.ufl24Pos || ''}
            onChange={(e) => handleChange('ufl24Pos', e.target.value)}
          >
            <option value="">Select Position</option>
            {positionOptions}
          </select>
        </div>
      </div>

      {/* Row: Draft Scout Data */}
      <div className="d-flex mt-2 gap-1">
        <div className="alert-navy px-2 py-1 w-100 rounded">
          <div className="fw-bold text-white bg-dark px-2 py-1 mb-2 rounded-top">Draft Scout Data</div>

          <div className="d-flex gap-1 align-items-end flex-nowrap">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">Rank</label>
              <input
                className={inputClass('rankphp', 'form-control fs-7')}
                type="text"
                value={player.rankphp || ''}
                onChange={(e) => handleChange('rankphp', e.target.value)}
                placeholder="Rank"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">Rank All</label>
              <input
                className={inputClass('rateAll', 'form-control fs-7')}
                type="text"
                value={player.rateAll || ''}
                onChange={(e) => handleChange('rateAll', e.target.value)}
                placeholder="Rank All"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">Proj. Rnd</label>
              <input
                className={inputClass('projRnd', 'form-control fs-7')}
                type="text"
                value={player.projRnd || ''}
                onChange={(e) => handleChange('projRnd', e.target.value)}
                placeholder="Proj. Rnd"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">Rate Date</label>
              <input
                className={inputClass('rateDate', 'form-control fs-7')}
                type="text"
                value={player.rateDate || ''}
                onChange={(e) => handleChange('rateDate', e.target.value)}
                placeholder="Rate Date"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">HS Stars</label>
              <input
                className={inputClass('hsStars', 'form-control fs-7')}
                type="text"
                value={player.hsStars || ''}
                onChange={(e) => handleChange('hsStars', e.target.value)}
                placeholder="HS Stars"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">DS Player ID</label>
              <input
                className={inputClass('draftScoutId', 'form-control fs-7')}
                type="text"
                value={player.draftScoutId || ''}
                onChange={(e) => handleChange('draftScoutId', e.target.value)}
                placeholder="DS Player ID"
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <label className="fs-7">CFL ID</label>
              <input
                className={inputClass('cflId', 'form-control fs-7')}
                type="text"
                value={player.cflId || ''}
                onChange={(e) => handleChange('cflId', e.target.value)}
                placeholder="CFL ID"
              />
            </div>
          </div>

          <div className="d-flex mt-2 gap-1">
            <div className="col-30">
              <label className="fs-7">Rank Note</label>
              <input
                className={inputClass('rankNote', 'form-control fs-7')}
                type="text"
                value={player.rateNotes || ''}
                onChange={(e) => handleChange('rateNotes', e.target.value)}
                placeholder="Rank Note"
              />
            </div>
            <div className="col-18">
              <label className="fs-7">Rank Source</label>
              <input
                className={inputClass('rankSource', 'form-control fs-7')}
                type="text"
                value={player.rateSource || ''}
                onChange={(e) => handleChange('rateSource', e.target.value)}
                placeholder="Rank Source"
              />
            </div>
          </div>
        </div>
      </div>
      {/* end */}
    </div>
  );
};

export default Player;
